/**
 * Pure geometry for the hero → header wordmark dock and the extended search
 * card's "top region" clamp. Kept free of DOM so it can be unit-tested; the
 * DOM-facing code lives in `docked-wordmark.tsx` / `text-area-search-bar.tsx`.
 */

export type Rect = { top: number; left: number; width: number; height: number };

/**
 * Where the wordmark INK sits inside the hero video's box, as fractions of the
 * box height. The renders are letterboxed and the header wordmark image is
 * cropped to the ink, so a FLIP between the two must map ink onto ink, not
 * box onto box — otherwise the lockup lands ~30% too low and visibly stretched.
 *
 * Light: the box shows source rows 0–515 of 630; ink spans rows 149–493
 * (see animated-title-video.tsx). Dark: object-[50%_30%] shows rows 81–530 of
 * 720; ink spans rows 215–507. Both round to the same ~4.27 aspect as the
 * header art (346×81 ink box in a 360×95 image — site-wordmark.tsx).
 */
export const HERO_INK_FRACTIONS = {
  light: { top: 149 / 515, bottom: 493 / 515 },
  dark: { top: (215 - 81) / (530 - 81), bottom: (507 - 81) / (530 - 81) },
} as const;

export const inkRectFromVideoBox = (box: Rect, theme: 'light' | 'dark' = 'light'): Rect => {
  const f = HERO_INK_FRACTIONS[theme];
  return {
    left: box.left,
    width: box.width,
    top: box.top + box.height * f.top,
    height: box.height * (f.bottom - f.top),
  };
};

/**
 * FLIP inverse transform that makes an element laid out at `last` appear at
 * `first`, with `transform-origin: 0 0`. Non-uniform scale on purpose: the two
 * assets are close but not identical in aspect, and a uniform scale would leave
 * a sliver of drift on one axis at the hand-off.
 */
export const flipTransform = (first: Rect, last: Rect): string => {
  const sx = last.width > 0 ? first.width / last.width : 1;
  const sy = last.height > 0 ? first.height / last.height : 1;
  const dx = first.left - last.left;
  const dy = first.top - last.top;
  return `translate(${round(dx)}px, ${round(dy)}px) scale(${round(sx, 4)}, ${round(sy, 4)})`;
};

/**
 * Inline style that makes `el` (laid out at `elRect`, untransformed) show its
 * ink — the sub-rectangle `inkRect` — exactly over `targetRect`. The transform
 * origin is pinned to the ink's top-left inside the element so scaling does not
 * drag the letterbox around it into the maths.
 */
export const flipInkOnto = (
  elRect: Rect,
  inkRect: Rect,
  targetRect: Rect,
): { transformOrigin: string; transform: string } => ({
  transformOrigin: `${round(inkRect.left - elRect.left)}px ${round(inkRect.top - elRect.top)}px`,
  transform: flipTransform(targetRect, inkRect),
});

/**
 * Max height for the card's top region so the card never runs under the sticky
 * nav: the space between the top of `#main-search-bar` (which starts right under
 * the nav at scroll 0) and the composer's top edge, minus a breathing gap. Same
 * 12px gap the old floating-panel clamp used.
 */
export const HERO_SLOT_GAP_PX = 12;
export const HERO_SLOT_MIN_PX = 120;

export const heroSlotMax = (rowTop: number, mainSearchBarTop: number, gap = HERO_SLOT_GAP_PX): number =>
  Math.max(HERO_SLOT_MIN_PX, Math.round(rowTop - mainSearchBarTop - gap));

/**
 * Phones: the card's conversation thread is as tall as the free screen allows.
 *
 * The card grows UP from the composer until its top reaches the header (the
 * slot above), and only then DOWN: the composer settles near the bottom of the
 * screen, the way a chat app has it, instead of leaving the thread the ~200px
 * between the header and a composer parked mid-screen.
 *
 * `cardTopPull` is how far the capsule pulls its top edge up for a region of
 * `height` (written to `--card-top-h`). Uncapped it is the whole region, which
 * keeps the composer row fixed; capped at the slot, whatever overflows it pushes
 * the composer down instead of running the card under the header.
 */
export const cardTopPull = (height: number, cap: number | null): number =>
  cap == null ? height : Math.min(height, cap);

/** The region's own bottom border, which counts toward the pull but not the thread. */
export const CARD_TOP_BORDER_PX = 1;

/** Clear air between the card's bottom edge and the bottom of the screen. */
export const PHONE_THREAD_BOTTOM_GAP_PX = 12;

/** A thread never gets squeezed below this, whatever the screen. */
export const PHONE_THREAD_MIN_PX = 96;

/**
 * Height of the thread (the `--thread-h` custom property): the card, grown to
 * its slot under the header, ends `PHONE_THREAD_BOTTOM_GAP_PX` above the
 * bottom of the screen. Everything in the card that is not the thread
 * (composer row, borders) is `chrome`.
 *
 * While a keyboard is up (`keyboardStrip` = the visual viewport's height) the
 * card has to fit the strip left above it instead, between the header and the
 * keyboard, the very band the keyboard lift centres a card in; `100svh` would
 * leave the composer under the keys.
 *
 * Without a keyboard the screen is `100svh` (the small viewport, constant while
 * browser toolbars come and go), as the hero's own offsets are.
 */
export const phoneThreadHeight = ({
  capsuleTop,
  chrome,
  headerBand,
  keyboardStrip,
  keyboardGap,
  headerGap,
}: {
  /** Document y of the capsule's top edge once the card has grown to its slot. */
  capsuleTop: number;
  chrome: number;
  headerBand: number;
  keyboardStrip: number | null;
  keyboardGap: number;
  headerGap: number;
}): string => {
  if (keyboardStrip != null) {
    const strip = keyboardStrip - (headerBand + headerGap) - keyboardGap - chrome;
    return `${Math.max(PHONE_THREAD_MIN_PX, Math.round(strip))}px`;
  }
  const reserved = Math.round(capsuleTop + chrome + PHONE_THREAD_BOTTOM_GAP_PX);
  return `max(${PHONE_THREAD_MIN_PX}px, calc(100svh - ${reserved}px))`;
};

const round = (n: number, digits = 2) => {
  const p = 10 ** digits;
  return Math.round(n * p) / p;
};
